import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Grid, Loader, Search } from "semantic-ui-react";
import BCButton from "../ui/BCButton";
import BCDashboard from "../ui/BCDashboard";
import BCFormInput from "../ui/BCFormInput";
import "../../styles/dashboard.scss";
import "../../styles/ui.scss";
import BWDrawRequestsContent from "./home/BWDrawRequestsContent";
import BWBorrowerStatementsContent from "./home/BWBorrowerStatementsContent";
import BWPaymentsContent from "./home/BWPaymentsContent";
import BWProjectsContent from "./home/BWProjectsContent";
import BCMessage from "../ui/BCMessage";
import { IProxyUser } from "../interfaces/Global";
import { deleteMockLoanDrawRequest, getMockRequestDrawBankInfolist, getMockRequestDrawLoanInfolist, validateMockLoanDraw } from "../../api/mock/MockBorrowerApi";
import { deleteLoanDrawRequest, getRequestDrawBankInfolist, getRequestDrawLoanInfolist, validateLoanDraw } from "../../api/BorrowerApi";
import BCModal from "../ui/BCModal";
import { getDropdownOptions, handleErrorResponse, handleFormChange } from "../../Helpers";
import BCValidationMessage from "../ui/BCValidationMessage";
import { GlobalConstants } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";

interface IBWHome {
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: any;
  setIsMessage: any;
  errorResponse: any;
  setErrorResponse: any;
  setSuccessMessage: any;
  proxyUser: IProxyUser | undefined;
  setCopiedApplicationId: React.Dispatch<React.SetStateAction<string | undefined>>; // TODO: probably not needed, unsure
}

const BWHome = ({
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage,
  setSuccessMessage,
  errorResponse, 
  setErrorResponse, 
  proxyUser
} : IBWHome) => {
  const [isApiMessage, setIsApiMessage] = useState([] as string[]);
  const [isModalLoading, setIsModalLoading] = useState(false as boolean);
  const [validateLoanDrawMessage, setValidateLoanDrawMessage] = useState(false as boolean);
  const [invalidProps, setInvalidProps] = useState([] as any);
  const [venderEmailMessage, setVenderEmailMessage] = useState(false as boolean);
  const [includeFundedDraws, setIncludeFundedDraws] = useState(false as boolean);
  const [activeOnlyBorrowerStatements, setActiveOnlyBorrowerStatements] = useState(false as boolean);
  const [activeOnlyPayments, setActiveOnlyPayments] = useState(false as boolean);
  const [activeOnlyProjects, setActiveOnlyProjects] = useState(false as boolean);
  const [isRequestDrawModalOpen, setIsRequestDrawModalOpen] = useState(false as boolean);
  const [loanDrawObj, setLoanDrawObj] = useState({} as any);
  const [requestDrawLoanInfo, setRequestDrawLoanInfo] = useState([] as any);
  const [requestDrawSelectedLoanProperties, setRequestDrawSelectedLoanProperties] = useState([] as any);
  const [requestDrawBankInfo, setRequestDrawBankInfo] = useState([] as any);

  const history = useHistory();
  const RequestDrawButton = () => {
    const handleRequestDraw = () => {
      setIsRequestDrawModalOpen(true);
    };
    return (
      <BCButton labelPosition="left" iconName="add" handleOnClick={() => handleRequestDraw()}>Request Draw</BCButton>
    );
  };

  const ViewAllButton = (props) => {
    const handleToEntireList = useCallback(() => {
      history.push(props.url);
    }, [history]);

    return (
      <BCButton handleOnClick={() => handleToEntireList()}>View All</BCButton>
    );
  };

  const handleSelectLoan = async (loanId, field) => {
    handleFormChange(loanId, field, loanDrawObj, setLoanDrawObj);
    const selectedLoan = requestDrawLoanInfo.find(loan => loan.id === loanId);
    if (selectedLoan) {
      setRequestDrawSelectedLoanProperties(selectedLoan.loanProperties);
      await getRequestDrawBankInfoAsync(loanId);
      setVenderEmailMessage(true);
    }
  };

  const handleSelectLoanProperty = async (loanPropertyId, field) => {
    handleFormChange(loanPropertyId, field, loanDrawObj, setLoanDrawObj);
    setValidateLoanDrawMessage(false);
    await validateLoanDrawAsync(loanPropertyId);
  };

  const handleSubmitLoanDrawRequest = async () => {
    setInvalidProps([]);
    const notValidProps = [] as string[];

    if (!loanDrawObj.loanId) {
      notValidProps.push("loanId");
    }

    if (!loanDrawObj.loanPropertiesId) {
      notValidProps.push("loanPropertiesId");
    }

    if (!loanDrawObj.bankId) {
      notValidProps.push("bankId");
    }

    if (notValidProps.length === 0) {
      // TODO: handler to add loan draw when endpoint works - Task 9518
      console.log("add loan draw async");
    }
    else {
      setTimeout(() => { 
        setInvalidProps(notValidProps);
      }, 50);
    }
  };

  const handleCloseRequestDrawModal = () => {
    setIsRequestDrawModalOpen(false);
    setValidateLoanDrawMessage(false);
    setVenderEmailMessage(false);
    setLoanDrawObj({});
    setInvalidProps([]);
  };

  const getRequestDrawBankInfoAsync = async (loanId) => {
    setIsModalLoading(true);
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockRequestDrawBankInfolist() : await getRequestDrawBankInfolist(loanId, proxyUser && proxyUser.id);
      if (response && response.ok) {
        const bankInfo = await response.json();
        setRequestDrawBankInfo(bankInfo);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      FeatureFlags.IS_DEBUG && console.log(error);
    }

    setIsModalLoading(false);
  };

  const validateLoanDrawAsync = async (loanPropertyId) => { // TODO: modify when actual endpoint works
    setIsModalLoading(true);
    try {
      const response = FeatureFlags.IS_MOCK ? await validateMockLoanDraw(loanPropertyId) : await validateLoanDraw(loanPropertyId, proxyUser && proxyUser.id);
      if (response && response.ok) {
        setIsModalLoading(false);
        setValidateLoanDrawMessage(false);
        return true;
      }
      else {
        await handleErrorResponse(response, setValidateLoanDrawMessage, setErrorResponse);
        setValidateLoanDrawMessage(true);
      }
    }
    catch (error) {
      FeatureFlags.IS_DEBUG && console.log(error);
      setValidateLoanDrawMessage(false);
    }

    setIsModalLoading(false);
    return false;
  };

  const deleteLoanDrawRequestAsync = async (loanDrawRequestId) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await deleteMockLoanDrawRequest() : await deleteLoanDrawRequest(loanDrawRequestId, proxyUser && proxyUser.id) as any;
      if (response && response.ok) {
        setSuccessMessage("Loan Draw Request has been successfully deleted.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const HomeLayout = [
    [
      {
        title: "Draw Requests",
        size: 16,
        headerOptions: [
          <BCFormInput
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            inputKey="draw-request-checkbox"
            checkboxText="Include Funded Draws"
            handleFormChange={() => setIncludeFundedDraws(!includeFundedDraws)}
          />,
          <Search open={false} onSearchChange={() => console.log("Draw Requests Search")} placeholder="Search" />,
          <RequestDrawButton />,
          <ViewAllButton url="/home/drawrequests" />
        ],
        content: <React.Fragment>
          <BWDrawRequestsContent 
            isApiMessage={isApiMessage}
            setIsApiMessage={setIsApiMessage}
            isGreaterThanTablet={isGreaterThanTablet}  
            proxyUser={proxyUser}
            includeFundedDraws={includeFundedDraws}
            deleteLoanDrawRequestAsync={deleteLoanDrawRequestAsync}
          />
        </React.Fragment>
      }
    ],
    [
      {
        title: "Borrower Statements",
        size: 8,
        headerOptions: [
          <BCFormInput
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            inputKey="borrower-statements-checkbox"
            checkboxText="Active Loans Only"
            handleFormChange={() => setActiveOnlyBorrowerStatements(!activeOnlyBorrowerStatements)}
          />,
          <Search 
            open={false} 
            onSearchChange={() => console.log("Borrower Statements Search")} 
            placeholder="Search" 
            className="bc-search-input-short"
          />,
          <ViewAllButton url="/home/borrowerstatements" />
        ],
        content: <React.Fragment>
          <BWBorrowerStatementsContent
            isApiMessage={isApiMessage}
            setIsApiMessage={setIsApiMessage}
            proxyUser={proxyUser}
            activeOnlyBorrowerStatements={activeOnlyBorrowerStatements}
          />
        </React.Fragment>
      },
      {
        title: "Payments",
        size: 8,
        headerOptions: [
          <BCFormInput
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            inputKey="payments-checkbox"
            checkboxText="Active Loans Only"
            handleFormChange={() => setActiveOnlyPayments(!activeOnlyPayments)}
          />,
          <Search 
            open={false} 
            onSearchChange={() => console.log("Payments Search")} 
            placeholder="Search" 
            className="bc-search-input-short"
          />,
          <ViewAllButton url="/home/payments" />
        ],
        content: <React.Fragment>
          <BWPaymentsContent 
            isApiMessage={isApiMessage}
            setIsApiMessage={setIsApiMessage}
            proxyUser={proxyUser}
            activeOnlyPayments={activeOnlyPayments}
          />
        </React.Fragment>
      }
    ],
    [
      {
        title: "Projects",
        size: 16,
        headerOptions: [<BCFormInput
          type={GlobalConstants.FormInputTypes.CHECKBOX}
          inputKey="projects-checkbox"
          checkboxText="Active Loans Only"
          handleFormChange={() => setActiveOnlyProjects(!activeOnlyProjects)}
        />,
          <Search open={false} onSearchChange={() => console.log("Projects Search")} placeholder="Search" />,
          <ViewAllButton url="/home/loanproject" />
        ],
        content: <React.Fragment>
          <BWProjectsContent 
            isApiMessage={isApiMessage}
            setIsApiMessage={setIsApiMessage}
            isGreaterThanTablet={isGreaterThanTablet}  
            proxyUser={proxyUser}
            activeOnlyProjects={activeOnlyProjects}
          />
        </React.Fragment>
      }
    ]
  ];

  const requestDrawModalContent = <Form className="primary-form" autoComplete="off">
    {FeatureFlags.IS_DEBUG && <React.Fragment>
      <button onClick={() => console.log(requestDrawBankInfo)}>requestDrawBankInfo</button>
      <button onClick={() => console.log(requestDrawLoanInfo)}>requestDrawLoanInfo</button>
      <button onClick={() => console.log(isModalLoading)}>isModalLoading</button>
      <button onClick={() => console.log(validateLoanDrawMessage)}>validateLoanDrawMessage</button>
    </React.Fragment>}
    {isModalLoading &&  <Loader className="modal-loader" active inline="centered" size={"big"} />} 
    <React.Fragment>
      <BCMessage
        animation={"fly down"}
        duration={750}
        visible={isMessage}
        transitionOnMount
        styles="full-width"
        onDismiss={() => setIsMessage(false)}
        errorResponse={errorResponse}
      />
      <BCMessage
        animation="fly down"
        duration={750}
        visible={validateLoanDrawMessage}
        transitionOnMount
        styles="full-width"
        color="red"
        onDismiss={() => setValidateLoanDrawMessage(false)}
        errorResponse={errorResponse}
        header="Operation failed"
        text="A Loan Draw request already exists for selected Loan and Property."
      />
      <Grid className="vendor-form-grid"> 
        <Grid.Row className="application-row">
          <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
            <BCFormInput 
              labelClass="vendor-form-label"
              type="dropdown"
              label="Loan:"
              placeholder="Please Select"
              obj={loanDrawObj}
              value={loanDrawObj.loanId}
              field="loanId"
              dropdownOptions={getDropdownOptions(requestDrawLoanInfo, ["id"], "display")}
              handleFormChange={(value, field) => handleSelectLoan(value, field)}
              required
            />
            <BCValidationMessage invalidProps={invalidProps} prop="loanId" propText="Please select a loan." />
          </Grid.Column>
          <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
            <BCFormInput 
              labelClass="vendor-form-label"
              type="dropdown"
              label="Loan Property:"
              placeholder="Please Select"
              obj={loanDrawObj}
              value={loanDrawObj.loanPropertiesId}
              field="loanPropertiesId"
              dropdownOptions={getDropdownOptions(requestDrawSelectedLoanProperties, ["id"], "display")}
              handleFormChange={(value, field) => handleSelectLoanProperty(value, field)}
              required
            />
            <BCValidationMessage invalidProps={invalidProps} prop="loanPropertiesId" propText="Please select a loan property." />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="application-row">
          <Grid.Column width={isGreaterThanMobile ? 16 : 16}>
          <BCFormInput 
              labelClass="vendor-form-label"
              type="dropdown"
              label="Bank:"
              placeholder="Please Select"
              obj={loanDrawObj}
              value={loanDrawObj.bankId}
              field="bankId"
              dropdownOptions={getDropdownOptions(requestDrawBankInfo, ["id"], "display")}
              handleFormChange={(value, field) => handleFormChange(value, field, loanDrawObj, setLoanDrawObj)}
              required
            />
            <BCValidationMessage invalidProps={invalidProps} prop="bankId" propText="Please select a bank." />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </React.Fragment>
    {venderEmailMessage && <React.Fragment>
      <p>The Vendor email you provide will receive an invitation to create an account in the BC Portal. The Vendor will be able to input their ACH information in order for draw funds to be disbursed directly to their accounts.</p>
      <p>As the Borrower, it is your responsibility to provide the correct Vendor email, and you are responsible for all payments made to the Vendor based on the ACH instructions inputted by the Vendor.</p>
    </React.Fragment>}
  </Form>;

  const requestDrawModalActions = <Grid>
    <Grid.Row>
      <Grid.Column width={8}>
        <Button
          className="bp-button"
          fluid
          onClick={() => handleSubmitLoanDrawRequest()}
        >
          <div className="gmr-button-container">
            <div className="gmr-button-text">Next</div>
          </div>
        </Button>
      </Grid.Column>
      <Grid.Column width={8}>
        <Button
          fluid
          onClick={() => handleCloseRequestDrawModal()}
        >
          <div className="gmr-button-container">
            <div className="gmr-button-text">Close</div>
          </div>
        </Button>
      </Grid.Column>
    </Grid.Row>
  </Grid>;

  useEffect(() => {
    const getRequestDrawLoanInfoAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockRequestDrawLoanInfolist() : await getRequestDrawLoanInfolist(proxyUser && proxyUser.id);
        if (response && response.ok) {
          const drawRequestsInfo = await response.json();
          setRequestDrawLoanInfo(drawRequestsInfo);
        }
        else {
          setIsApiMessage([...isApiMessage, "Draw Requests"]);
        }
      }
      catch (error) {
        console.log(error);
        
      }
    };

    getRequestDrawLoanInfoAsync();
  }, [proxyUser]);

    return (
      <div className={(isGreaterThanTablet && isSidebar) ? "hide-sidebar" : "sidebar"}>
        <BCMessage
          animation="fly down"
          duration={750}
          visible={isApiMessage.length > 0}
          transitionOnMount
          styles="full-width"
          color="red"
          header="Oops!"
          text="It looks like something went wrong."
        />
        <BCModal
          isModalOpen={isRequestDrawModalOpen}
          onClose={() => handleCloseRequestDrawModal()}
          onOpen={() => setIsRequestDrawModalOpen(true)}
          header="Loan Draw"
          content={requestDrawModalContent}
          actions={requestDrawModalActions}
        />
        <BCDashboard
          DashboardLayout={HomeLayout}
          isGreaterThanMobile={isGreaterThanMobile}
          dashboardHeaderOptions={true}
        />
        {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(requestDrawLoanInfo)}>requestDrawLoanInfo</button>
      </React.Fragment>}
    </div>
    );
  };
  
export default BWHome;